import React, { useMemo, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import moment from "moment";

import defaultImage from "../../Assets/images/piLogo.png";

// firebase
import { db } from "../../firebase";

// styles
import styles from "./detail.module.scss";

// dummy data
import { feed } from "../dashboard/dummyData";

// icons
import { FaSearch } from "react-icons/fa";
import {
  MdModeEdit,
  MdRemoveRedEye,
  MdRestoreFromTrash,
  MdCloudUpload,
  MdVideocam,
} from "react-icons/md";
import { Image, Row, Col } from "react-bootstrap";
import { EditProfileModal } from "./EditProfileModal";
import { PlayerReports } from "./reports/PlayerReports";
import Header from "../header/header";
import { useParams, useHistory } from "react-router-dom";
import { CustomSpinner } from "../shared/Spinner";
import { BackgroundOverview } from "./background-overview/BackgroundOverview";
import { ScoutingOverview } from "./scouting-overview/ScoutingOverview";
import { toast } from "react-toastify";
import { isDisplayableIndustryRankValue, formatWeightDisplay } from "../../utils/helpers";
import { formatProfileAge } from "../../utils/age";
import log_activity from "../activity/logger";
import DeleteModal from "../shared/DeleteModal";
import { notificationService } from "../../Services/notification-service";
import { RoleAdjustments } from "./RoleAdjustments";
import { MeasurementsSection } from "./MeasurementsSection";
import { KeyPlayerContactsSection } from "./KeyPlayerContactsSection";
import { useAuth } from "../../Context/AuthContext";
import { formatFeetInches } from "../../utils/feetInches";

const formatCompositeRankDisplay = (value) => {
  if (value === null || value === undefined) return value;
  const s = String(value).trim();
  if (!s) return s;
  if (/^#\d+$/.test(s)) return s;
  const ordinal = s.match(/^(\d{1,4})(st|nd|rd|th)$/i);
  if (ordinal) return `#${ordinal[1]}`;
  if (/^\d{1,4}$/.test(s)) return `#${s}`;
  return s;
};

const hasIndustryRankingUrl = (url) => typeof url === "string" && url.trim() !== "";

/** Admin detail: show linked "N/A" when a source URL exists but no rank was scraped. */
const getIndustryRankingDisplayText = (item, isAdminDetailView) => {
  if (isDisplayableIndustryRankValue(item.value)) {
    return formatCompositeRankDisplay(item.value);
  }
  if (isAdminDetailView && hasIndustryRankingUrl(item.url)) return "N/A";
  return formatCompositeRankDisplay(item.value);
};

export const PlayerProfileDetail = () => {
  const { isAdmin, isSuperAdmin } = useAuth();
  const history = useHistory();
  const [openEditProfile, setOpenEditProfile] = useState(false);
  const [playerProfileData, setPlayerProfileData] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedDelete, setSelectedDelete] = useState(false);
  const [backgroundOverviewLoaded, setBackgroundOverviewLoaded] = useState(false); // New state
  const [scoutingReportCount, setScoutingReportCount] = useState(0); // scouting counter
  const [intelReportCount, setIntelReportCount] = useState(0); // intel counter
  const [scoutingOverviewCount, setScoutingOverviewCount] = useState(0); // counter
  const [backgroundOverviewCompletion, setBackgroundOverviewCompletion] = useState(0);// completion percentage
  const [gameLogStats, setGameLogStats] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);


  const profileCompletion = useMemo(() => {
    if (!playerProfileData) return 0;
  
    let total = 0;
  
    // Player Information
    if (playerProfileData.FirstName) total += 1.5;
    if (playerProfileData.LastName) total += 1.5;
    if (playerProfileData.hometown_City) total += 5;
    if (playerProfileData.hometown_State_Country) total += 5;
    if (playerProfileData.Team) total += 5;
    if (playerProfileData.Country) total += 5;
    if (playerProfileData.Year) total += 5;
    if (playerProfileData.DOB) total += 10;
    if (playerProfileData.Position) total += 5;
    if (playerProfileData.Height) total += 5;
    if (playerProfileData.Weight) total += 5;
    if (playerProfileData.Wingspan) total += 5;
    if (playerProfileData.viewing_privileges) total += 2;
    if (playerProfileData.Headshot) total += 10;
  
    // Social Media
    if (playerProfileData.Twitter) total += 1.5;
    if (playerProfileData.Snapchat) total += 2;
    if (playerProfileData.Instagram) total += 5;
    if (playerProfileData.TikTok) total += 1.5;
  
    // AAU Information
    if (playerProfileData.aauTeam) total += 5;
  
    // Recruitment Status
    if (playerProfileData.Status) total += 5;
  
    // Projection Range
    if (playerProfileData.pRange) total += 5;
  
    // Video
    if (playerProfileData.video1) total += 5;
  
    return Math.min(total, 100);
  }, [playerProfileData]);

  const isAdminDetailView = isAdmin() || isSuperAdmin();

  const compositeRankingItems = useMemo(() => {
    const rankings = playerProfileData?.compositeRankings || {};
    // Temporarily hide On3/Rivals until Cloudflare blocking is resolved for the scraper.
    const items = [
      {
        key: "rsci",
        label: "RSCI",
        value: rankings.rsci,
        url: playerProfileData?.rivalsRankingUrl || "",
      },
      {
        key: "ranking247",
        label: "247Sports",
        value: rankings.ranking247,
        url: playerProfileData?.ranking247Url || "",
      },
      {
        key: "espn",
        label: "ESPN",
        value: rankings.espn,
        url: playerProfileData?.espnRankingUrl || "",
      },
    ];

    const adminView = isAdmin() || isSuperAdmin();
    if (adminView) {
      return items.filter(
        (item) =>
          isDisplayableIndustryRankValue(item.value) || hasIndustryRankingUrl(item.url)
      );
    }
    return items.filter((item) => isDisplayableIndustryRankValue(item.value));
  }, [playerProfileData, isAdmin, isSuperAdmin]);

  const hasCompositeRankings = compositeRankingItems.length > 0;


  const { id } = useParams();

  useEffect(() => {
    if (id) {
      getPlayerProfile();
    }
    return () => {};
  }, [id]);

  useEffect(() => {
    if (id) {
      const fetchGameLogs = async () => {
        try {
          const snapshot = await db
            .collection("game-log-stats")
            .where("playerProfileId", "==", id)
            .get();
          const logs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setGameLogStats(logs);
        } catch (error) {
          console.error("Error fetching game logs:", error);
        }
      };
      fetchGameLogs();
    }
  }, [id]);
  
  useEffect(() => {
    // Check if background overview data exists
    const hasBackgroundOverview = Boolean(playerProfileData && playerProfileData.backgroundOverview);
    setBackgroundOverviewLoaded(hasBackgroundOverview);
  }, [playerProfileData]);

  useEffect(() => {
    if (id) {
      const unsubscribe = db.collection("reports")
        .where("playerProfile", "==", id)  // Match playerProfile ID field
        .where("type", "==", "Scouting")   // Match report type
        .onSnapshot((snapshot) => {
          console.log('Scouting reports:', snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
          setScoutingReportCount(snapshot.size);
        }, (error) => {
          console.error("Error fetching scouting reports:", error);
        });
  
      return () => unsubscribe();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      const unsubscribe = db.collection("reports")
        .where("playerProfile", "==", id)  // Match playerProfile ID field
        .where("type", "==", "Intel")   // Match report type
        .onSnapshot((snapshot) => {
          console.log('Intel reports:', snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
          setIntelReportCount(snapshot.size);
        }, (error) => {
          console.error("Error fetching Intel reports:", error);
        });
  
      return () => unsubscribe();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      const unsubscribe = db.collection("scouting-overview")
        .where("playerProfile", "==", id)
        .onSnapshot((snapshot) => {
          console.log('Scouting overview docs:', snapshot.docs);
          // Set to 1 if any documents exist, 0 otherwise
          setScoutingOverviewCount(snapshot.empty ? 0 : 1);
        }, (error) => {
          console.error("Error checking scouting overview:", error);
        });

      return () => unsubscribe();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      const unsubscribe = db.collection("background-overview")
        .where("playerProfile", "==", id)
        .onSnapshot((snapshot) => {
          if (!snapshot.empty) {
            const backgroundData = snapshot.docs[0].data();
            calculateBackgroundCompletion(backgroundData);
          } else {
            setBackgroundOverviewCompletion(0);
          }
        }, (error) => {
          console.error("Error fetching background overview:", error);
        });
  
      return () => unsubscribe();
    }
  }, [id]);

  const GameLogStatsTable = ({ stats }) => {
    const [sortConfig, setSortConfig] = useState({
      key: 'date',
      direction: 'desc',
      compositeState: {}
    });
    const [typeFilter, setTypeFilter] = useState('ALL');

    const typeFilterOptions = ['ALL', 'HS', 'AAU', 'CAMP', 'INTL', 'OTHER'];

    // Helper function to check if a stat entry has no statistical data (player didn't play)
    const hasNoStats = (stat) => {
      // Check if minutes exist and have a meaningful value
      const minValue = stat.min;
      const hasMinutes = minValue && 
        minValue !== '' && 
        minValue !== null && 
        minValue !== undefined &&
        String(minValue).trim() !== '' &&
        String(minValue).trim() !== '0:00' &&
        String(minValue).trim() !== '0:00:00';
      
      // If player has meaningful minutes, they played - don't filter out
      if (hasMinutes) return false;
      
      // Check all other stat fields - if any has a non-zero value, player played
      const statFields = [
        'fgFga', 'fgPct', 'threePThreePA', 'threeFgPct', 
        'ftFta', 'ftPct', 'orb', 'drb', 'trb', 'ast', 'blk', 'stl', 'to', 'pf', 'pts'
      ];
      
      // If any stat field has a non-zero value, player played
      const hasAnyStat = statFields.some(field => {
        const value = stat[field];
        // Check if value is empty, null, undefined, 0, or empty string
        if (value === null || value === undefined || value === '') return false;
        if (typeof value === 'string') {
          // For composite fields like "1-2", check if it has non-zero values
          if (value.includes('-')) {
            const parts = value.split('-').map(Number);
            return parts[0] > 0 || parts[1] > 0;
          }
          // For regular strings, check if it's not empty
          return value.trim() !== '';
        }
        // For numbers, check if it's non-zero
        const num = parseFloat(value);
        return !isNaN(num) && num > 0;
      });
      
      // If no minutes and no stats, player didn't play - filter out
      return !hasAnyStat;
    };

    const columns = [
      { key: 'date', title: "Date", type: 'date' },
      { key: 'season', title: "Season", type: 'string' },
      { key: 'type', title: "Type", type: 'string', isFilter: true },
      { key: 'event', title: "Event", type: 'string' },
      { key: 'team', title: "Team", type: 'string' },
      { key: 'opponent', title: "Opponent", type: 'string' },
      { key: 'result', title: "Result", type: 'string' },
      { 
        key: 'min', 
        title: "MIN", 
        type: 'number',
        render: (value) => {
          if (!value) return '--';
          const str = String(value);
          // Remove trailing ":00" if present (e.g., "35:15:00" -> "35:15")
          return str.replace(/:00$/, '');
        }
      },
      { 
        key: 'fgFga', 
        title: "FG-FGA",
        composite: true,
        type: 'composite'
      },
      { 
        key: 'fgPct', 
        title: "FG%",
        type: 'number',
        render: (value) => {
          const num = parseFloat(value);
          return !isNaN(num) ? num.toFixed(1) : '--';
        }
      },
      { 
        key: 'threePThreePA', 
        title: "3P-3PA",
        composite: true,
        type: 'composite'
      },
      { 
        key: 'threeFgPct', 
        title: "3FG%",
        type: 'number',
        render: (value) => {
          const num = parseFloat(value);
          return !isNaN(num) ? num.toFixed(1) : '--';
        }
      },
      { 
        key: 'ftFta', 
        title: "FT-FTA",
        composite: true,
        type: 'composite'
      },
      { 
        key: 'ftPct', 
        title: "FT%",
        type: 'number',
        render: (value) => {
          const num = parseFloat(value);
          return !isNaN(num) ? num.toFixed(1) : '--';
        }
      },
      { key: 'orb', title: "ORB", type: 'number' },
      { key: 'drb', title: "DRB", type: 'number' },
      { key: 'trb', title: "TRB", type: 'number' },
      { key: 'ast', title: "AST", type: 'number' },
      { key: 'blk', title: "BLK", type: 'number' },
      { key: 'stl', title: "STL", type: 'number' },
      { key: 'to', title: "TO", type: 'number' },
      { key: 'pf', title: "PF", type: 'number' },
      { key: 'pts', title: "PTS", type: 'number' }
    ];

    const handleSort = (column) => {
      setSortConfig(prev => {
        const isComposite = column.composite;
        const currentState = prev.compositeState[column.key] || 0;
        
        if (prev.key !== column.key) {
          return {
            key: column.key,
            direction: isComposite ? 'asc' : 'asc',
            compositeState: {
              ...prev.compositeState,
              [column.key]: isComposite ? 1 : 1
            }
          };
        }

        if (isComposite) {
          const nextState = (currentState + 1) % 5;
          if (nextState === 0) {
            return {
              key: 'date',
              direction: 'desc',
              compositeState: {
                ...prev.compositeState,
                [column.key]: 0
              }
            };
          }
          
          return {
            key: column.key,
            direction: nextState % 2 === 1 ? 'asc' : 'desc',
            compositeState: {
              ...prev.compositeState,
              [column.key]: nextState
            }
          };
        } else {
          const nextState = (currentState + 1) % 3;
          if (nextState === 0) {
            return {
              key: 'date',
              direction: 'desc',
              compositeState: {
                ...prev.compositeState,
                [column.key]: 0
              }
            };
          }
          
          return {
            key: column.key,
            direction: nextState === 1 ? 'asc' : 'desc',
            compositeState: {
              ...prev.compositeState,
              [column.key]: nextState
            }
          };
        }
      });
    };

    // Apply filtering and sorting
    const filteredAndSortedStats = useMemo(() => {
      try {
        // First filter out stats with no data (player didn't play)
        const statsWithData = stats.filter(stat => !hasNoStats(stat));
        
        // Then apply type filter
        const filteredStats = typeFilter === 'ALL' 
          ? [...statsWithData] 
          : statsWithData.filter(stat => stat.type === typeFilter);

        // Then apply sorting
        const { key, direction, compositeState } = sortConfig;
        const column = columns.find(col => col.key === key);
        const currentCompositeState = compositeState[key] || 0;

        return filteredStats.sort((a, b) => {
          // Handle composite fields (FG-FGA, 3P-3PA, FT-FTA)
          if (column?.composite) {
            // Parse the string values ("1-2") into numbers
            const parseComposite = (value) => {
              if (!value) return [0, 0];
              const parts = String(value).split('-').map(Number);
              return [parts[0] || 0, parts[1] || 0];
            };
            
            const [aFirst, aSecond] = parseComposite(a[key]);
            const [bFirst, bSecond] = parseComposite(b[key]);
            
            switch (currentCompositeState) {
              case 1: return aFirst - bFirst;
              case 2: return bFirst - aFirst;
              case 3: return aSecond - bSecond;
              case 4: return bSecond - aSecond;
              default: return 0;
            }
          }
          
          // Handle regular fields
          let aVal = a[key];
          let bVal = b[key];
          
          // Handle different data types
          if (column?.type === 'date') {
            aVal = new Date(aVal);
            bVal = new Date(bVal);
            return direction === 'asc' ? aVal - bVal : bVal - aVal;
          } 
          else if (column?.type === 'string') {
            aVal = String(aVal || '');
            bVal = String(bVal || '');
            return direction === 'asc' 
              ? aVal.localeCompare(bVal) 
              : bVal.localeCompare(aVal);
          } 
          else { // numbers
            aVal = parseFloat(aVal) || 0;
            bVal = parseFloat(bVal) || 0;
            return direction === 'asc' ? aVal - bVal : bVal - aVal;
          }
        });
      } catch (error) {
        console.error("Sorting error:", error);
        return stats;
      }
    }, [stats, sortConfig, typeFilter]);

    // Calculate totals and averages for filtered stats
    const calculateTotalsAndAverages = useMemo(() => {
      // First filter out stats with no data (player didn't play)
      const statsWithData = stats.filter(stat => !hasNoStats(stat));
      
      // Then apply type filter
      const filteredStats = typeFilter === 'ALL' 
        ? [...statsWithData] 
        : statsWithData.filter(stat => stat.type === typeFilter);

      if (filteredStats.length === 0) {
        return { totals: {}, averages: {}, gamesPlayed: 0, record: { wins: 0, losses: 0 } };
      }

      const totals = {
        min: 0,
        fgMade: 0,
        fgAttempted: 0,
        threePMade: 0,
        threePAttempted: 0,
        ftMade: 0,
        ftAttempted: 0,
        orb: 0,
        drb: 0,
        trb: 0,
        ast: 0,
        blk: 0,
        stl: 0,
        to: 0,
        pf: 0,
        pts: 0
      };

      let wins = 0;
      let losses = 0;

      filteredStats.forEach(stat => {
        // Parse minutes (format: "MM:SS" or "MM:SS:00")
        const minParts = (stat.min || '0:00').split(':');
        const minutes = parseInt(minParts[0]) + (parseInt(minParts[1]) / 60);
        totals.min += minutes;

        // Parse composite fields
        const parseComposite = (value) => {
          if (!value) return [0, 0];
          const parts = String(value).split('-').map(Number);
          return [parts[0] || 0, parts[1] || 0];
        };

        const [fgMade, fgAttempted] = parseComposite(stat.fgFga);
        const [threePMade, threePAttempted] = parseComposite(stat.threePThreePA);
        const [ftMade, ftAttempted] = parseComposite(stat.ftFta);

        totals.fgMade += fgMade;
        totals.fgAttempted += fgAttempted;
        totals.threePMade += threePMade;
        totals.threePAttempted += threePAttempted;
        totals.ftMade += ftMade;
        totals.ftAttempted += ftAttempted;

        // Add other stats
        totals.orb += parseFloat(stat.orb) || 0;
        totals.drb += parseFloat(stat.drb) || 0;
        totals.trb += parseFloat(stat.trb) || 0;
        totals.ast += parseFloat(stat.ast) || 0;
        totals.blk += parseFloat(stat.blk) || 0;
        totals.stl += parseFloat(stat.stl) || 0;
        totals.to += parseFloat(stat.to) || 0;
        totals.pf += parseFloat(stat.pf) || 0;
        totals.pts += parseFloat(stat.pts) || 0;

        // Count wins/losses
        const result = stat.result || '';
        if (result.startsWith('W')) wins++;
        else if (result.startsWith('L')) losses++;
      });

      const gamesPlayed = filteredStats.length;
      const averages = {
        min: gamesPlayed > 0 ? (totals.min / gamesPlayed).toFixed(1) : 0,
        fgMade: gamesPlayed > 0 ? (totals.fgMade / gamesPlayed).toFixed(1) : 0,
        fgAttempted: gamesPlayed > 0 ? (totals.fgAttempted / gamesPlayed).toFixed(1) : 0,
        fgPct: totals.fgAttempted > 0 ? ((totals.fgMade / totals.fgAttempted) * 100).toFixed(1) : 0,
        threePMade: gamesPlayed > 0 ? (totals.threePMade / gamesPlayed).toFixed(1) : 0,
        threePAttempted: gamesPlayed > 0 ? (totals.threePAttempted / gamesPlayed).toFixed(1) : 0,
        threeFgPct: totals.threePAttempted > 0 ? ((totals.threePMade / totals.threePAttempted) * 100).toFixed(1) : 0,
        ftMade: gamesPlayed > 0 ? (totals.ftMade / gamesPlayed).toFixed(1) : 0,
        ftAttempted: gamesPlayed > 0 ? (totals.ftAttempted / gamesPlayed).toFixed(1) : 0,
        ftPct: totals.ftAttempted > 0 ? ((totals.ftMade / totals.ftAttempted) * 100).toFixed(1) : 0,
        orb: gamesPlayed > 0 ? (totals.orb / gamesPlayed).toFixed(1) : 0,
        drb: gamesPlayed > 0 ? (totals.drb / gamesPlayed).toFixed(1) : 0,
        trb: gamesPlayed > 0 ? (totals.trb / gamesPlayed).toFixed(1) : 0,
        ast: gamesPlayed > 0 ? (totals.ast / gamesPlayed).toFixed(1) : 0,
        blk: gamesPlayed > 0 ? (totals.blk / gamesPlayed).toFixed(1) : 0,
        stl: gamesPlayed > 0 ? (totals.stl / gamesPlayed).toFixed(1) : 0,
        to: gamesPlayed > 0 ? (totals.to / gamesPlayed).toFixed(1) : 0,
        pf: gamesPlayed > 0 ? (totals.pf / gamesPlayed).toFixed(1) : 0,
        pts: gamesPlayed > 0 ? (totals.pts / gamesPlayed).toFixed(1) : 0
      };

      return {
        totals: {
          ...totals,
          fgPct: totals.fgAttempted > 0 ? ((totals.fgMade / totals.fgAttempted) * 100).toFixed(1) : 0,
          threeFgPct: totals.threePAttempted > 0 ? ((totals.threePMade / totals.threePAttempted) * 100).toFixed(1) : 0,
          ftPct: totals.ftAttempted > 0 ? ((totals.ftMade / totals.ftAttempted) * 100).toFixed(1) : 0
        },
        averages,
        gamesPlayed,
        record: { wins, losses }
      };
    }, [stats, typeFilter]);

    // Get sort indicator symbol
    const getSortSymbol = (columnKey) => {
      if (sortConfig.key !== columnKey) return '';
      
      if (columnKey === 'date' && sortConfig.direction === 'desc') {
        return ' ↓';
      }
      
      const currentState = sortConfig.compositeState[columnKey] || 0;
      
      if (currentState === 0) return '';
      if (currentState % 2 === 1) return ' ↑';
      return ' ↓';
    };

    return (
      <div className={styles.feed}>
        <h2>Stats</h2>
        <hr />
        <div className={styles.tableContainer}>
          <table>
            <thead>
              <tr>
                {columns.map(column => (
                  <th 
                    key={column.key}
                    onClick={() => column.isFilter ? null : handleSort(column)}
                    style={{ cursor: column.isFilter ? 'default' : 'pointer' }}
                  >
                    {column.isFilter ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{column.title}</span>
                        <select 
                          value={typeFilter} 
                          onChange={(e) => setTypeFilter(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          style={{ 
                            padding: '2px 4px', 
                            fontSize: '12px',
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            backgroundColor: 'white'
                          }}
                        >
                          {typeFilterOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <>
                        {column.title}
                        {getSortSymbol(column.key)}
                      </>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedStats.map((row, rowIndex) => (
                <tr key={`row-${rowIndex}`}>
                  {columns.map(column => {
                    if (column.key === "result") {
                      const result = row.result || '';
                      const resultLetter = result.charAt(0) || "-";
                      const rest = result.substring(1).trim() || "";
                      const isWin = resultLetter === "W";
                      
                      return (
                        <td key={`${rowIndex}-${column.key}`}>
                          <span style={{ color: isWin ? "green" : "red", fontWeight: 600 }}>
                            {resultLetter}
                          </span>{" "}
                          {rest}
                        </td>
                      );
                    }
                    
                    const value = row[column.key];
                    
                    if (column.render) {
                      return (
                        <td key={`${rowIndex}-${column.key}`}>
                          {column.render(value)}
                        </td>
                      );
                    }
                    
                    // For composite fields, just display the string as-is
                    if (column.composite) {
                      return (
                        <td key={`${rowIndex}-${column.key}`}>
                          {value || "--"}
                        </td>
                      );
                    }
                    
                    return (
                      <td key={`${rowIndex}-${column.key}`}>
                        {value || "--"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  

  const calculateBackgroundCompletion = (backgroundData) => {
    let total = 0;
  
    // Team History
    if (backgroundData.teamHistory) {
      total += backgroundData.teamHistory.length * 10;
    }
  
    // National Team Experience
    if (backgroundData.nationalTeamExperience) {
      const nte = backgroundData.nationalTeamExperience;
      if (nte.country) total += 1;
      if (nte.experiences) total += nte.experiences.length;
    }
  
    // Awards & Honors
    if (backgroundData.awards) {
      total += backgroundData.awards.length;
    }
  
    // Recruitment
    if (backgroundData.recruitment) {
      const rec = backgroundData.recruitment;
      if (rec.commitment && rec.month && rec.year) total += 5;
      if (rec.notableInterests) total += rec.notableInterests.length;
      if (rec.notableOffers) total += rec.notableOffers.length;
      if (rec.notableVisits) total += rec.notableVisits.length;
      if (rec.notes && rec.notes.trim()) total += 5;
    }
  
    // Personal
    if (backgroundData.personal) {
      const personal = backgroundData.personal;
      if (personal.familyCircleMembers) {
        total += personal.familyCircleMembers.length * 3;
      }
      if (personal.nuggets) {
        total += personal.nuggets.length * 2;
      }
    }
  
    // Social Media
    if (backgroundData.socialMedia) {
      const sm = backgroundData.socialMedia;
      
      // Instagram
      if (sm.instagram?.account) {
        total += 3;
        if (sm.instagram.nuggets) {
          total += sm.instagram.nuggets.length;
        }
      }
      
      // Twitter
      if (sm.twitter?.account) {
        total += 2.5;
        if (sm.twitter.dms) total += 2.5;
        if (sm.twitter.nuggets) {
          total += sm.twitter.nuggets.length;
        }
      }
    }
  
    // Additional Notes
    if (backgroundData.additionalNotes?.trim()) {
      total += 5;
    }
  
    // Cap at 100%
    total = Math.min(total, 100);
    setBackgroundOverviewCompletion(total);
  };
  

  
  

  const getPlayerProfile = async () => {
    setLoading(true);
    try {
      const response = await db.collection("player-profile").doc(id).get();
      const data = response.data();
      //console.log("Player Profile Data:", data); // Log data to check background overview information
      setPlayerProfileData(data);
      setLoading(false);
    } catch (error) {
      console.log(error, "error");
      setLoading(false);
    }
  };

  const handleDelete = () => {
    db.collection("player-profile")
      .doc(id)
      .delete()
      .then(async () => {
        toast.error("Player Profile Deleted Successfully", {
          theme: "colored",
        });
        const watchlistDoc = await db.collection("watchlist").doc(id).get();
        if (watchlistDoc.exists) {
          const receipients = watchlistDoc.data().subscribers;
          const playerName =
            (playerProfileData.FirstName ?? "").trim() +
            " " +
            (playerProfileData.LastName ?? "").trim();
          notificationService.send(
            receipients,
            `${playerName}'s profile was deleted.`,
            id
          );
        }
        log_activity(
          `${playerProfileData.FirstName} ${playerProfileData.LastName}`,
          "Player Profile was Deleted"
        );
        history.push("/player-profile");
      })
      .catch((error) => {
        toast.error("Error Deleting Player Profile", {
          theme: "colored",
        });
      });
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (loading) {
    return <CustomSpinner />;
  }

  if (playerProfileData === null && loading === false) {
    return <div>Loading Data</div>;
  }

  return (
    <>
      <DeleteModal
        handleClose={() => {
          setSelectedDelete(false);
        }}
        show={selectedDelete}
        handleConfirm={() => {
          handleDelete();
        }}
      />
      <div className={styles.container}>
        <Header />
        {/* content */}
        <div className={styles.title}>
          <h2>Player Profile</h2>
          <hr />
        </div>
        {/* Intro */}
        <Row className="">
          {windowWidth <= 600 ? (
            // Mobile Layout
            <Col xs={12}>
              <div className={styles.profileInto}>
                <div className={styles.mobileProfileHeader}>
                  <div className={styles.mobileEyeIcon}>
                    <MdRemoveRedEye
                      onClick={() => {
                        history.push(`/player-profile/preview/${id}`);
                      }}
                    />
                  </div>
                  <div className={styles.mobileProfileImage}>
                    <Image
                      width="120px"
                      height="120px"
                      style={{
                        objectFit: "cover",
                      }}
                      roundedCircle
                      src={playerProfileData.Headshot && playerProfileData.Headshot.trim() !== "" && playerProfileData.Headshot !== "null" ? playerProfileData.Headshot : defaultImage}
                    />
                  </div>
                  <div className={styles.mobileProfileInfo}>
                    <h2 className={styles.mobilePlayerName}>
                      {playerProfileData.FirstName} {playerProfileData.LastName}
                    </h2>
                    <p className={styles.mobileLocation}>
                      {playerProfileData.hometown_City}
                      {playerProfileData.hometown_City && playerProfileData.hometown_State_Country ? ", " : ""}
                      {playerProfileData.hometown_State_Country}
                    </p>
                    <p className={styles.mobileTeam}>
                      {playerProfileData.Team}
                    </p>
                    <p className={styles.mobileStatus}>
                      {playerProfileData.Status !== "Undecided" ? `${playerProfileData.Status}: ${playerProfileData.committedTeam}` : playerProfileData.Status}
                    </p>
                  </div>
                  <div className={styles.mobileProjectionRange}>
                    <p>Projection Range</p>
                    <h4>
                      {playerProfileData.pRange &&
                      playerProfileData.Prlow !== "" &&
                      playerProfileData.Prhigh !== ""
                        ? playerProfileData.Prlow === playerProfileData.Prhigh
                          ? playerProfileData.Prhigh
                          : `${playerProfileData.Prlow} to ${playerProfileData.Prhigh}`
                        : "-"}
                    </h4>
                  </div>

                  {hasCompositeRankings && (
                    <div className={styles.mobileProjectionRange}>
                      <p>Industry Rankings</p>
                      <div className={styles.compositeRankingListMobile}>
                        {compositeRankingItems.map((item) => (
                          <div key={item.key} className={styles.compositeRankingListItem}>
                            <span>{item.label}</span>
                            {item.url ? (
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.compositeRankingLink}
                              >
                                {getIndustryRankingDisplayText(item, isAdminDetailView)}
                              </a>
                            ) : (
                              <strong>{getIndustryRankingDisplayText(item, isAdminDetailView)}</strong>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className={styles.mobileStatsGrid}>
                    <div className={styles.mobileStatItem}>
                      <span>HT</span>
                      <strong>{formatFeetInches(playerProfileData.Height) || "--"}</strong>
                    </div>
                    <div className={styles.mobileStatItem}>
                      <span>WT</span>
                      <strong>
                        {formatWeightDisplay(playerProfileData.Weight)}
                      </strong>
                    </div>
                    <div className={styles.mobileStatItem}>
                      <span>WS</span>
                      <strong>{formatFeetInches(playerProfileData.Wingspan) || "--"}</strong>
                    </div>
                    <div className={styles.mobileStatItem}>
                      <span>CLASS</span>
                      <strong>{playerProfileData.Year || "--"}</strong>
                    </div>
                    <div className={styles.mobileStatItem}>
                      <span>HAND</span>
                      <strong>{playerProfileData.Hand || "--"}</strong>
                    </div>
                    <div className={styles.mobileStatItem}>
                      <span>POS</span>
                      <strong>{playerProfileData.Position || "--"}</strong>
                    </div>
                    <div className={styles.mobileStatItem}>
                      <span>DOB</span>
                      <strong>{playerProfileData.DOB ? moment(playerProfileData.DOB).format("MM/DD/YY") : "--"}</strong>
                    </div>
                    <div className={styles.mobileStatItem}>
                      <span>AGE</span>
                      <strong>{formatProfileAge(playerProfileData.DOB, playerProfileData.Age)}</strong>
                    </div>
                    <div className={`${styles.mobileStatItem} ${styles.piScoreMobile}`}>
                      <span>P.I. SCORE</span>
                      <strong>Coming Soon</strong>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.profileComplete}>
                <h2>Profile Completion</h2>
                <div className={styles.tableData}>
                  {[
                    {
                      title: "Profile",
                      desc: `${profileCompletion.toFixed(1)}%`,
                    },
                    {
                      title: "BO",
                      desc: backgroundOverviewCompletion > 0 ? `${backgroundOverviewCompletion}%` : "-",
                    },
                    {
                      title: "SO",
                      desc: scoutingOverviewCount > 0 ? "✔️" : "-",
                    },
                  ].map((el, ind) => (
                    <div key={ind}>
                      <p>{el.title}</p>
                      <h4>{el.desc}</h4>
                    </div>
                  ))}
                </div>
                <div className={styles.tableData}>
                  {[
                    {
                      title: "Scouting",
                      desc: scoutingReportCount > 0 ? `${scoutingReportCount}` : "-",
                    },
                    {
                      title: "Intel",
                      desc: intelReportCount > 0 ? `${intelReportCount}` : "0",
                    },
                    {
                      title: "Stats",
                      desc: gameLogStats.length > 0 ? "✔️" : "-",
                    },
                  ].map((el, ind) => (
                    <div key={ind}>
                      <p>{el.title}</p>
                      <h4>{el.desc}</h4>
                    </div>
                  ))}
                </div>
              </div>
            </Col>
          ) : (
            // Desktop Layout (unchanged)
            <>
              <Col md={9}>
                <div className={styles.profileInto}>
                  <Row>
                    <Col
                      md={2}
                      className="d-flex justify-content-center align-items-center"
                    >
                      <Image
                        width="100px"
                        height="100px"
                        style={{
                          objectFit: "cover",
                        }}
                        roundedCircle
                        src={playerProfileData.Headshot && playerProfileData.Headshot.trim() !== "" && playerProfileData.Headshot !== "null" ? playerProfileData.Headshot : defaultImage}
                      />
                    </Col>
                    <Col md={10}>
                      <div className="d-flex justify-content-between">
                        <div className={styles.tablePersonInfo}>
                          <h4>
                            {playerProfileData.FirstName}{" "}
                            {playerProfileData.LastName}
                          </h4>
                          <h6>{playerProfileData.Team}</h6>
                          <p>
                            {playerProfileData.hometown_City}
                            {playerProfileData.hometown_City && playerProfileData.hometown_State_Country ? ", " : ""}
                            {playerProfileData.hometown_State_Country}
                          </p>
                        </div>
                        <div>
                          <MdRemoveRedEye
                            onClick={() => {
                              history.push(`/player-profile/preview/${id}`);
                            }}
                          />
                          {windowWidth > 600 && (
                            <>
                              <MdModeEdit
                                onClick={() => {
                                  setOpenEditProfile(true);
                                }}
                              />
                              <MdRestoreFromTrash
                                onClick={() => {
                                  setSelectedDelete(true);
                                }}
                              />
                            </>
                          )}
                        </div>
                      </div>
                      <div className={styles.tableData}>
                        <div>
                          <p>Nationality</p>
                          <h4>{playerProfileData.Country ? playerProfileData.Country : "--"}</h4>
                        </div>
                        <div>
                          <p>Position</p>
                          <h4>{playerProfileData.Position ? playerProfileData.Position : "--"}</h4>
                        </div>
                        <div>
                          <p>Year</p>
                          <h4>{playerProfileData.Year ? playerProfileData.Year : "--" }</h4>
                        </div>
                        <div>
                          <p>DOB</p>
                          <h4>{playerProfileData.DOB ? moment(playerProfileData.DOB).format("l") : '--'}</h4>
                        </div>
                        <div>
                          <p>Age</p>
                          <h4>{formatProfileAge(playerProfileData.DOB, playerProfileData.Age)}</h4>
                        </div>
                        <div>
                          <p>AAU Team</p>
                          <h4 style={{ textAlign: 'center' }}>{playerProfileData.aauTeam ? playerProfileData.aauTeam : "--" }</h4>
                        </div>
                        <div>
                          <p>Affiliation</p>
                          <h4>{playerProfileData.aauAffil ? playerProfileData.aauAffil : "--"}</h4>
                        </div>
                        <div>
                          <p>Status</p>
                          <h4 style={{ textAlign: 'center' }}>
                          {playerProfileData.Status !== "Undecided" ? `${playerProfileData.Status}: ${playerProfileData.committedTeam}` : playerProfileData.Status}
                          </h4>
                        </div>
                        <div className={styles.tableDataDark}>
                          <p>P.I. Score</p>
                      
                          <h4 style={{ textAlign: 'center' }}>
                            {(isAdmin() || isSuperAdmin()) && playerProfileData.overallScore
                              ? playerProfileData.overallScore
                              : "Coming Soon"}
                          </h4>
                          {/*<h4>{playerProfileData.Piscore}</h4>*/}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
              <Col md={3}>
                <div className={styles.profileComplete} style={{ marginTop: windowWidth <= 600 ? '20px' : '0' }}>
                  <h2>Profile Completion</h2>
                  <div className={styles.tableData}>
                    {[
                      {
                        title: "Profile",
                        desc: `${profileCompletion.toFixed(1)}%`,
                      },
                      {
                        title: "BO",
                        desc: backgroundOverviewCompletion > 0 ? `${backgroundOverviewCompletion}%` : "-",
                        //desc: "-%",
                      },
                      {
                        title: "SO",
                        //desc: "- or ✔️",
                        desc: scoutingOverviewCount > 0 ? "✔️" : "-",
                      },
                    ].map((el, ind) => (
                      <div key={ind}>
                        <p>{el.title}</p>
                        <h4>{el.desc}</h4>
                      </div>
                    ))}{" "}
                  </div>
                  <div className={styles.tableData}>
                    {[
                      {
                        title: "Scouting",
                        desc: scoutingReportCount > 0 ? `${scoutingReportCount}` : "-",
                      },
                      {
                        title: "Intel",
                        desc: intelReportCount > 0 ? `${intelReportCount}` : "0",
                      },
                      {
                        title: "Stats",
                        desc: gameLogStats.length > 0 ? "✔️" : "-",
                      },
                    ].map((el, ind) => (
                      <div key={ind}>
                        <p>{el.title}</p>
                        <h4>{el.desc}</h4>
                      </div>
                    ))}{" "}
                  </div>
                </div>
              </Col>
            </>
          )}
        </Row>
        {windowWidth > 600 && hasCompositeRankings && (
          <div className={styles.feed}>
            <h2>Industry Rankings</h2>
            <div className={styles.compositeRankingSectionBody}>
              <div className={styles.compositeRankingList}>
                {compositeRankingItems.map((item) => (
                  <div key={item.key} className={styles.compositeRankingListItem}>
                    <span>{item.label}</span>
                    {item.url ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.compositeRankingLink}
                      >
                        {getIndustryRankingDisplayText(item, isAdminDetailView)}
                      </a>
                    ) : (
                      <strong>{getIndustryRankingDisplayText(item, isAdminDetailView)}</strong>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {/* Measurements & key contacts: admin / super-admin only for now */}
        {windowWidth > 600 && (isAdmin() || isSuperAdmin()) && (
          <MeasurementsSection
            playerID={id}
            playerName={`${playerProfileData.FirstName} ${playerProfileData.LastName}`}
            playerDOB={playerProfileData.DOB ?? playerProfileData.dob}
          />
        )}
        {windowWidth > 600 && (isAdmin() || isSuperAdmin()) && (
          <KeyPlayerContactsSection
            playerID={id}
            playerName={`${playerProfileData.FirstName} ${playerProfileData.LastName}`}
          />
        )}
        {/* Background Overview  */}
        {windowWidth > 600 && (
          <BackgroundOverview
            playerID={id}
            playerName={`${playerProfileData.FirstName} ${playerProfileData.LastName}`}
          />
        )}
        {/* end */}
        {/* Scouting Overview */}
        {windowWidth > 600 && (
          <ScoutingOverview
            playerID={id}
            playerName={`${playerProfileData.FirstName} ${playerProfileData.LastName}`}
          />
        )}
        {/* Reports */}
        {windowWidth > 600 && (
          <PlayerReports playerID={id} playerProfileData={playerProfileData} />
        )}

        {/* Role Adjustments */}
        {windowWidth > 600 && (
          <div className={styles.roleAdjustments}>
            <RoleAdjustments 
              playerID={id} 
              playerName={`${playerProfileData.FirstName} ${playerProfileData.LastName}`}
            />
          </div>
        )}

        {windowWidth > 600 && gameLogStats.length > 0 && (
          <GameLogStatsTable stats={gameLogStats} />
        )}

        {/* Stats
        <div
          className={styles.feed}
          style={{
            marginTop: "20px",
          }}
        >
          <h2>Stats</h2>
          <hr />
          <div className="d-flex justify-content-end">
            <div className={styles.filterCard}>
              <div>Sort By</div>
              <div
                style={{
                  width: "40px",
                }}
              >
                <FaSearch />
              </div>
            </div>
          </div>
          <div className={styles.tableContainer}>
            <table>
              <thead>
                <tr>
                  <th>Created Date</th>
                  <th>Report Type</th>
                  <th>Note Date</th>
                  <th>Preview</th>
                  <th>Editor</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {feed.map((item, index) => (
                  <tr key={index}>
                    <td>{item.date}</td>
                    <td>{item.player}</td>
                    <td>{item.type}</td>
                    <td>{item.editor}</td>
                    <td>{item.time}</td>
                    <td>
                      <div className={styles.actionIcon}>
                        <MdRemoveRedEye />
                        <MdModeEdit
                          style={{
                            color: "green",
                          }}
                        />
                        <MdRestoreFromTrash
                          style={{
                            color: "red",
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        */}
        {/* gallery 
        <Row>
          <Col md={6}>
            <div className={styles.uploadSection}>
              <h2>Gallery</h2>
              <hr />
              <label htmlFor="caption">Caption</label>
              <br />
              <input type="text" id="caption" name="caption" />
              <StyledDropzone />
            </div>
          </Col>
          <Col md={6}>
            <div className={styles.uploadSection}>
              <h2>Video</h2>
              <hr />
              <label htmlFor="caption">Caption</label>
              <br />
              <input type="text" id="caption" name="caption" />
              <StyledDropzone video />
            </div>
          </Col>
        </Row>
        {/* Edit Profile Section */}
        {playerProfileData && (
          <EditProfileModal
            key={id}
            show={openEditProfile}
            getPlayerProfile={getPlayerProfile}
            docID={id}
            handleClose={() => setOpenEditProfile(false)}
            item={playerProfileData}
          />
        )}{" "}
      </div>
    </>
  );
};

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  borderWidth: 2,
  borderRadius: 2,
  borderColor: "#eeeeee",
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  outline: "none",
  transition: "border .24s ease-in-out",
  marginTop: "15px",
  width: "100%",
};

const focusedStyle = {
  borderColor: "#2196f3",
};

const acceptStyle = {
  borderColor: "#00e676",
};

const rejectStyle = {
  borderColor: "#ff1744",
};

function StyledDropzone(props) {
  const {
    acceptedFiles,
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone();

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );
  const files = acceptedFiles.map((file) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
    </li>
  ));

  return (
    <>
      <div {...getRootProps({ style })}>
        <input {...getInputProps()} />
        {props.video ? (
          <MdVideocam style={{ fontSize: "24px" }} />
        ) : (
          <MdCloudUpload style={{ fontSize: "24px" }} />
        )}
        <p>Upload file smaller than 3MB</p>
      </div>
      {acceptedFiles.length > 0 && (
        <aside>
          <h6 className="mt-2">Files</h6>
          <ul>{files}</ul>
        </aside>
      )}{" "}
    </>
  );
}

